import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import { queryClient } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { HypothesisCreativeEntry, HypothesisSection, } from '../types/hypothesis';

export interface UpdateHypothesisSectionPayload {
  creative_id: number;
  creative_type: string;
  test_period: HypothesisSection;
}

interface ErrorResponse {
  message: string;
  details?: string;
}

export const updateHypothesisSection = async (
  payload: UpdateHypothesisSectionPayload,
): Promise<void> => {
  const res = await apiClient.post(
    queryKeys.hypothesisSection.join('/'),
    payload,
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to update hypothesis section');
  }
};

export interface UpdateHypothesisSectionVariables {
  creatives: Pick<HypothesisCreativeEntry, 'creative_id' | 'creative_type'>[];
  test_period: HypothesisSection;
}

const updateHypothesisSectionBatch = async (
  variables: UpdateHypothesisSectionVariables,
): Promise<void> => {
  await Promise.all(
    variables.creatives.map((creative) =>
      updateHypothesisSection({
        creative_id: Number(creative.creative_id),
        creative_type: creative.creative_type,
        test_period: variables.test_period,
      }),
    ),
  );
};

interface UseUpdateHypothesisSectionOptions {
  config?: MutationConfig<typeof updateHypothesisSectionBatch>;
}

interface OptimisticContext {
  snapshots: { queryKey: readonly unknown[]; data: HypothesisCreativeEntry[] | undefined }[];
}

export const useUpdateHypothesisSection = (
  { config }: UseUpdateHypothesisSectionOptions = {},
) => {
  return useMutation({
    ...config,
    mutationFn: updateHypothesisSectionBatch,
    onMutate: async ({ creatives, test_period }): Promise<OptimisticContext> => {
      await queryClient.cancelQueries({ queryKey: queryKeys.allTestCreatives });

      const matches = queryClient.getQueriesData<HypothesisCreativeEntry[]>({
        queryKey: queryKeys.allTestCreatives,
      });

      const snapshots = matches.map(([queryKey, data]) => ({
        queryKey,
        data,
      }));

      const targetIds = new Set(creatives.map((c) => c.creative_id));

      for (const [queryKey, data] of matches) {
        if (!data) continue;
        const next = data.map((creative) =>
          targetIds.has(creative.creative_id)
            ? { ...creative, section: test_period }
            : creative,
        );
        queryClient.setQueryData<HypothesisCreativeEntry[]>(queryKey, next);
      }

      return { snapshots };
    },
    onError: (...args) => {
      const ctx = args[2];
      if (ctx) {
        for (const { queryKey, data } of ctx.snapshots) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      config?.onError?.(...args);
    },
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.allTestCreatives,
      });
      config?.onSuccess?.(...args);
    },
  });
};
